import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import Link, { LinkProps } from 'next/link'
import { cn } from '@utils/index'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none text-sm  text-red-100 font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none relative w-fit bg-airclaw-light border border-airclaw-stroke text-airclaw-foreground text-lg tracking-wider pr-6 pl-4 py-3 transition-colors duration-200 hover:bg-airclaw-light/10',
  {
    variants: {
      variant: {
        default: 'bg-airclaw-light',
      },
      size: {
        default: 'h-10 p-2',
        sm: 'h-9 px-3',
        lg: 'h-11 px-8',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

type ButtonBaseProps = {
  children: React.ReactNode
  className?: string
  back?: boolean
  loading?: boolean
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  asChild?: boolean
} & VariantProps<typeof buttonVariants> &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'>

type ButtonVariantProps = ButtonBaseProps & {
  as?: 'button'
}

type LinkVariantProps = ButtonBaseProps &
  LinkProps & {
    as: 'link'
  }

type ButtonProps = ButtonVariantProps | LinkVariantProps

const AirclawButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      as,
      children,
      loading,
      startIcon,
      endIcon,
      ...props
    },
    ref,
  ) => {
    const slant = 'calc(100% - 15px)'
    const commonStyles = {
      clipPath: `polygon(0 0, 100% 0, 100% 70%, ${slant} 100%, 0 100%)`,
    }

    const renderContent = () => (
      <>
        <div className="absolute top-.25 -right-[11px] h-[200%] w-px bg-airclaw-stroke z-10 rotate-[51.5deg]" />
        <span className="relative z-10 inline-flex items-center gap-2">
          {startIcon && startIcon}
          {children}
          {endIcon && !loading && endIcon}
          {loading && (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          )}
        </span>
      </>
    )

    if (as === 'link') {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size, className }))}
          style={commonStyles}
        >
          <Link {...(props as LinkProps)}>{renderContent()}</Link>
        </Slot>
      )
    }

    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        style={commonStyles}
        {...(props as ButtonVariantProps)}
      >
        {renderContent()}
      </Comp>
    )
  },
)

AirclawButton.displayName = 'AirclawButton'

export { AirclawButton }
